import {
  all,
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import {
  orgScopesService,
  orgsService,
  scopeExpansionService,
  shareService,
} from "../services";
import {
  ModelWithMeta,
  PagedResponse,
  QueryParams,
  PatchWithMeta,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import { Organisation } from "../domain/Organisation";
import {
  ActionPayload,
  editApplicationError,
  editApplicationSuccess,
  editOrganisationEngagementError,
  editOrganisationEngagementSuccess,
  editOrganisationError,
  editOrganisationSuccess,
  loadedApplication,
  loadedApplicationScopes,
  loadedExpansionApplicationList,
  loadedOrganisation,
  loadedOrganisationList,
  loadedOrganisationScopes,
  loadedShare,
  loadingApplication,
  loadingOrganisation,
} from "./actions";
import {
  APPROVING_CHANGE,
  CLOSE_APPLICATION,
  EDIT_ORGANISATION,
  EDIT_ORGANISATION_ENGAGEMENT,
  ADD_ORGANISATION_ENGAGEMENT_EVENT,
  LOADING_ORGANISATION,
  LOADING_ORGANISATION_LIST,
  RELEASE_APPLICATION,
  EDIT_ORGANISATION_ENGAGEMENT_EVENT,
  REOPEN_APPLICATION,
  SENDING_MESSAGE,
  SENDING_ATTACHMENTS,
  LOADING_APPLICATION,
  EDIT_APPLICATION,
  APPROVE_APPLICATION,
  REJECT_APPLICATION,
  WITHDRAW_APPLICATION,
  LOADING_EXPANSION_APPLICATION_LIST,
  LOADING_ORGANISATION_SCOPES,
  LOADING_APPLICATION_SCOPES,
  CREATE_SCOPES,
  ASSIGN_ORGANISATION,
  UNASSIGNING_ORGANISATION,
  UPDATING_SHARE,
} from "./constants";
import { applicationSelector, authSelector, orgSelector } from "./selectors";
import { CloseApplication } from "../domain/CloseApplication";
import {
  refreshActivityFeed,
  waitActivityFeed,
} from "@ofqual/portal-activity-feed";
import { OrganisationEngagementEvent } from "../domain/OrganisationEngagementEvent";
import { RecognitionApplication } from "../domain/RecognitionApplication";
import { ApproveApplication } from "../domain/ApproveApplication";
import { RejectApplication } from "../domain/RejectApplication";
import {
  OrganisationState,
  NonNullableRecognitionApplicationState,
} from "./state";
import { ScopeApplicationSummary } from "../domain/ScopeApplication";
import { Scope } from "@ofqual/portal-organisations-scope/dist/module/domain/Scope";
import { ShareRecord } from "../domain/ShareRecord";

// allow some time for the css fade out of a read notification to complete
const FADE_OUT_MS = 250;

function* watchGetAllSaga() {
  yield takeLatest(LOADING_ORGANISATION_LIST, getAllSaga);
}

function* watchGetOrganisationSaga() {
  yield takeLatest(LOADING_ORGANISATION, getOrganisationSaga);
}

function* watchPatchOrganisationSaga() {
  yield takeLatest(EDIT_ORGANISATION, patchOrganisationSaga);
}

function* watchPatchEngagementSaga() {
  yield takeLatest(EDIT_ORGANISATION_ENGAGEMENT, patchEngagementSaga);
}

function* watchPutEngagementEventSaga() {
  yield takeLatest(
    ADD_ORGANISATION_ENGAGEMENT_EVENT,
    putEngagementEventByItemIdSaga
  );
}

function* watchPatchEngagementEventSaga() {
  yield takeLatest(
    EDIT_ORGANISATION_ENGAGEMENT_EVENT,
    patchEngagementEventByItemIdSaga
  );
}

function* watchApproveChangeSaga() {
  yield takeEvery(APPROVING_CHANGE, approveChangeSaga);
}

function* watchCloseApplicationSaga() {
  yield takeEvery(CLOSE_APPLICATION, closeApplicationSaga);
}

function* watchReopenApplicationSaga() {
  yield takeEvery(REOPEN_APPLICATION, reopenApplicationSaga);
}

function* watchReleaseApplicationSaga() {
  yield takeEvery(RELEASE_APPLICATION, releaseApplicationSaga);
}

function* watchSendMessageSaga() {
  yield takeEvery(SENDING_MESSAGE, sendMessageSaga);
}

function* watchAttachmentsSaga() {
  yield takeLatest(SENDING_ATTACHMENTS, attachmentsSaga);
}

function* watchGetApplicationSaga() {
  yield takeLatest(LOADING_APPLICATION, getApplicationSaga);
}

function* watchPatchApplicationSaga() {
  yield takeLatest(EDIT_APPLICATION, patchApplicationSaga);
}

function* watchApproveApplicationSaga() {
  yield takeEvery(APPROVE_APPLICATION, approveApplicationSaga);
}

function* watchRejectApplicationSaga() {
  yield takeEvery(REJECT_APPLICATION, rejectApplicationSaga);
}

function* watchWithdrawApplicationSaga() {
  yield takeEvery(WITHDRAW_APPLICATION, withdrawApplicationSaga);
}

function* watchExpansionApplicationListSaga() {
  yield takeLatest(
    LOADING_EXPANSION_APPLICATION_LIST,
    expansionApplicationListSaga
  );
}

function* watchGetOrganisationScopesSaga() {
  yield takeLatest(LOADING_ORGANISATION_SCOPES, getOrganisationScopesSaga);
}

function* watchGetApplicationScopesSaga() {
  yield takeLatest(LOADING_APPLICATION_SCOPES, getApplicationScopesSaga);
}

function* watchPostApplicationScopesSaga() {
  yield takeLatest(CREATE_SCOPES, postApplicationScopes);
}

function* watchAssignOrganisationSaga() {
  yield takeLatest(ASSIGN_ORGANISATION, postAssignOrganisation);
}
function* watchUnAssignOrganisationSaga() {
  yield takeLatest(UNASSIGNING_ORGANISATION, postUnAssignOrganisation);
}
function* watchUpdateShareSaga() {
  yield takeLatest(UPDATING_SHARE, updateShareSaga);
}

export function* getAllSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  const response: PagedResponse<Organisation> = yield call(
    [orgsService, orgsService.search],
    payload,
    {
      token: auth.token,
    }
  );

  yield put(loadedOrganisationList(response));
}

export function* getOrganisationSaga({ payload }: ActionPayload<any>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const opts = { token: auth.token };

  const organisation: Organisation = yield call(
    [orgsService, orgsService.getOrganisationByItemId],
    payload,
    opts
  );
  const share: ShareRecord = yield call(
    [shareService, shareService.get],
    organisation.id,
    opts
  );

  yield put(loadedOrganisation(organisation, share));
}

export function* putEngagementEventByItemIdSaga({
  payload,
}: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  const response: OrganisationEngagementEvent = yield call(
    [orgsService, orgsService.putEngagementEventByItemId],
    payload.orgId,
    payload.newEvent,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(response.orgId));
}

export function* patchEngagementEventByItemIdSaga({
  payload,
}: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  const response: OrganisationEngagementEvent = yield call(
    [orgsService, orgsService.patchEngagementEventByItemId],
    payload.orgId,
    payload.updatedEvent,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(response.orgId));
}

export function* patchOrganisationSaga({ payload }: ActionPayload<any>): any {
  try {
    const { token }: ReturnType<typeof authSelector> = yield select(
      authSelector
    );
    // Start off with the original org in case we don't have to make any API calls

    if (payload.document.length !== 0) {
      var patchMeta: PatchWithMeta<Organisation> = {
        document: payload.document,
        meta: payload.meta,
      };
      const response: ValidatedResponse<Organisation> = yield call(
        [orgsService, orgsService.patchOrganisationByItemId],
        payload.itemId,
        patchMeta,
        {
          token,
        }
      );
      if (response.isValid) {
        yield put(refreshActivityFeed(response.data.id));
        return yield put(editOrganisationSuccess(response.data));
      } else {
        return yield put(editOrganisationError(response.error));
      }
    } else {
      yield delay(100);
      let resultOrg = (yield select(orgSelector)).org;
      yield put(refreshActivityFeed(resultOrg.id));
      return yield put(editOrganisationSuccess(resultOrg));
    }
  } catch (err: any) {
    return yield put(editOrganisationError(err));
  }
}

export function* patchEngagementSaga({ payload }: ActionPayload<any>): any {
  try {
    const { token }: ReturnType<typeof authSelector> = yield select(
      authSelector
    );
    // Start off with the original org in case we don't have to make any API calls

    if (payload.patchDoc.length !== 0) {
      const response: ValidatedResponse<Organisation> = yield call(
        [orgsService, orgsService.patchEngagementByItemId],
        payload.itemId,
        payload.patchDoc,
        {
          token,
        }
      );
      if (response.isValid) {
        return yield put(editOrganisationEngagementSuccess(response.data));
      } else {
        return yield put(editOrganisationEngagementError(response.error));
      }
    } else {
      yield delay(100);
      let resultOrg = (yield select(orgSelector)).org;
      return yield put(editOrganisationEngagementSuccess(resultOrg));
    }
  } catch (err: any) {
    return yield put(editOrganisationEngagementError(err));
  }
}

function* approveChangeSaga({
  payload,
}: ActionPayload<{ id: string; changeId: string; isApprove: boolean }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, changeId, isApprove } = payload;
  // We do an 'all' with a delay to make sure that the reading of a notifaction
  //  takes *at least* NOTIFICATION_FADE_OUT_MS.  We may as well have the api call going in
  //  parrallel with the delay.  If the delay finishes first, we have a long running api call
  //  so it was a good thing we weren't doing it sequentially after the delay had finished.
  //  If the api call finishes first, it was super quick and so we still need to wait for the
  //  delay to finish to give the UI time to fade out.
  const { organisation } = yield all({
    artificialDelay: delay(FADE_OUT_MS), //todo: could the delay length be passed in the payload from the UI?
    organisation: call(
      [
        orgsService,
        isApprove ? orgsService.approveChange : orgsService.rejectChange,
      ],
      id,
      changeId,
      {
        token,
      }
    ),
  });

  yield put(loadedOrganisation(organisation));
}

function* closeApplicationSaga({
  payload,
}: ActionPayload<{
  id: string;
  modelWithMeta: ModelWithMeta<CloseApplication>;
}>) {
  const { token } = yield select(authSelector);
  const { id, modelWithMeta } = payload;
  yield put(waitActivityFeed(id));
  const organisation: Organisation = yield call(
    [orgsService, orgsService.closeApplication],
    id,
    modelWithMeta,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedOrganisation(organisation));
}

function* reopenApplicationSaga({
  payload,
}: ActionPayload<{ id: string; notes: string }>) {
  const { token } = yield select(authSelector);

  const { id, notes } = payload;
  yield put(waitActivityFeed(id));
  const organisation: Organisation = yield call(
    [orgsService, orgsService.reopenApplication],
    id,
    { notes },
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedOrganisation(organisation));
}

function* releaseApplicationSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;
  yield put(waitActivityFeed(id));
  const organisation: Organisation = yield call(
    [orgsService, orgsService.releaseApplication],
    id,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedOrganisation(organisation));
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ id: string; message: string }>) {
  const { token } = yield select(authSelector);
  const { id, message } = payload;
  yield put(waitActivityFeed(id));
  yield call([orgsService, orgsService.sendMessage], id, message, {
    token,
  });

  yield put(refreshActivityFeed(id));
}

function* attachmentsSaga({
  payload,
}: ActionPayload<{ id: string; files: File[]; note: string }>) {
  const { token } = yield select(authSelector);
  const { id, files, note } = payload;
  yield put(waitActivityFeed(id));
  yield call([orgsService, orgsService.sendAttachments], id, files, note, {
    token,
  });

  yield put(refreshActivityFeed(id));
}

export function* getApplicationSaga({ payload }: ActionPayload<any>) {
  const { orgId, applicationId } = payload;
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  const {
    application,
    organisation,
    attachments,
    reviewAttachments,
    panelAttachments,
  } = yield all({
    application: call(
      [orgsService, orgsService.getApplication],
      orgId,
      applicationId,
      {
        token,
      }
    ),
    organisation: call(
      [orgsService, orgsService.getApplicationOrganistion],
      orgId,
      applicationId,
      {
        token,
      }
    ),
    attachments: call(
      [orgsService, orgsService.getApplicationAttachments],
      orgId,
      applicationId,
      {
        token,
      }
    ),
    reviewAttachments: call(
      [orgsService, orgsService.getApplicationReviewAttachments],
      orgId,
      applicationId,
      {
        token,
      }
    ),
    panelAttachments: call(
      [orgsService, orgsService.getApplicationPanelAttachments],
      orgId,
      applicationId,
      {
        token,
      }
    ),
  });

  application.organisation = organisation;
  application.attachments = attachments.data;
  application.reviewAttachments = reviewAttachments.data;
  application.panelAttachments = panelAttachments.data;

  yield put(loadedApplication(application));
}

function* patchApplicationSaga({ payload }: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const {
    orgId,
    applicationId,
    patchDocument,
    newReviewAttachments,
    newPanelAttachments,
    deletedReviewAttachmentIds,
    deletedPanelAttachmentIds,
  } = payload as {
    orgId: string;
    applicationId: string;
    patchDocument: PatchWithMeta<RecognitionApplication>;
    newReviewAttachments: File[];
    newPanelAttachments: File[];
    deletedReviewAttachmentIds: string[];
    deletedPanelAttachmentIds: string[];
  };

  yield put(waitActivityFeed(orgId));

  const reviewAttachmentsCall = () =>
    newReviewAttachments.length
      ? orgsService.submitReviewDocuments(
          orgId,
          applicationId,
          newReviewAttachments,
          {
            token,
          }
        )
      : Promise.resolve();

  const panelAttachmentsCall = () =>
    newPanelAttachments.length
      ? orgsService.submitPanelDocuments(
          orgId,
          applicationId,
          newPanelAttachments,
          {
            token,
          }
        )
      : Promise.resolve();

  const deleteReviewAttachmentsCalls = () =>
    Promise.all(
      deletedReviewAttachmentIds.map((id) =>
        orgsService.deleteReviewDocument(orgId, applicationId, id, {
          token,
        })
      )
    );

  const deletePanelAttachmentsCalls = () =>
    Promise.all(
      deletedPanelAttachmentIds.map((id) =>
        orgsService.deletePanelDocument(orgId, applicationId, id, {
          token,
        })
      )
    );

  const patchCall = () =>
    orgsService.patchApplication(orgId, applicationId, patchDocument, {
      token,
    });

  const { validatedResponse } = yield all({
    validatedResponse: call(patchCall),
    reviewAttachments: call(reviewAttachmentsCall),
    panelAttachments: call(panelAttachmentsCall),
    deletedReviewAttachments: call(deleteReviewAttachmentsCalls),
    deletedPanelAttachments: call(deletePanelAttachmentsCalls),
  });

  if (!validatedResponse.isValid) {
    yield put(editApplicationError(validatedResponse.error));
  } else {
    const { reviewAttachments, panelAttachments } = yield all({
      reviewAttachments: call(
        [orgsService, orgsService.getApplicationReviewAttachments],
        orgId,
        applicationId,
        {
          token,
        }
      ),
      panelAttachments: call(
        [orgsService, orgsService.getApplicationPanelAttachments],
        orgId,
        applicationId,
        {
          token,
        }
      ),
    });

    // lets retain orgs details and "attachments" as they are read only
    const {
      application: existingApplication,
    }: NonNullableRecognitionApplicationState = yield select(
      applicationSelector
    );

    const application: RecognitionApplication = validatedResponse.data;
    application.reviewAttachments = reviewAttachments.data;
    application.panelAttachments = panelAttachments.data;
    application.organisation = existingApplication.organisation;
    application.attachments = existingApplication.attachments;

    yield put(refreshActivityFeed(orgId));
    yield put(editApplicationSuccess(application));
  }
}

function* approveApplicationSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  applicationId: string;
  approveApplication: ApproveApplication;
}>) {
  const { token } = yield select(authSelector);
  const { orgId, applicationId, approveApplication } = payload;
  yield put(waitActivityFeed(orgId));
  yield call(
    [orgsService, orgsService.approveApplication],
    orgId,
    applicationId,
    approveApplication,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(orgId));
  yield put(loadingApplication(orgId, applicationId));
  const orgState: OrganisationState = yield select(orgSelector);
  if (orgState.org) {
    yield put(loadingOrganisation(String(orgState.org.itemId)));
  }
}

function* rejectApplicationSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  applicationId: string;
  rejectApplication: ModelWithMeta<RejectApplication>;
}>) {
  const { token } = yield select(authSelector);
  const { orgId, applicationId, rejectApplication } = payload;
  yield put(waitActivityFeed(orgId));
  yield call(
    [orgsService, orgsService.rejectApplication],
    orgId,
    applicationId,
    rejectApplication,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(orgId));
  yield put(loadingApplication(orgId, applicationId));
  const orgState: OrganisationState = yield select(orgSelector);
  if (orgState.org) {
    yield put(loadingOrganisation(String(orgState.org.itemId)));
  }
}

function* withdrawApplicationSaga({
  payload,
}: ActionPayload<{ orgId: string; applicationId: string }>) {
  const { token } = yield select(authSelector);
  const { orgId, applicationId } = payload;
  yield put(waitActivityFeed(orgId));
  yield call(
    [orgsService, orgsService.withdrawApplication],
    orgId,
    applicationId,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(orgId));
  yield put(loadingApplication(orgId, applicationId));
  const orgState: OrganisationState = yield select(orgSelector);
  if (orgState.org) {
    yield put(loadingOrganisation(String(orgState.org.itemId)));
  }
}

function* expansionApplicationListSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { orgId, queryParams } = payload as {
    orgId: string;
    queryParams: QueryParams;
  };

  const pagedResponse: PagedResponse<ScopeApplicationSummary> = yield call(
    [scopeExpansionService, scopeExpansionService.search],
    orgId,
    queryParams,
    { token }
  );
  yield put(loadedExpansionApplicationList(pagedResponse, orgId));
}

export function* getOrganisationScopesSaga({ payload }: ActionPayload<any>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  const response: Scope[] = yield call(
    [orgScopesService, orgScopesService.get],
    payload,
    {
      token,
    }
  );

  yield put(loadedOrganisationScopes(response));
}

export function* getApplicationScopesSaga({
  payload,
}: ActionPayload<{ orgId: string; applicationId: string }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { orgId, applicationId } = payload;

  const response: Scope[] = yield call(
    [orgsService, orgsService.getApplicationScopes],
    orgId,
    applicationId,
    {
      token,
    }
  );

  yield put(loadedApplicationScopes(response));
}

export function* postApplicationScopes({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);

  const response: Scope[] = yield call(
    [orgsService, orgsService.postScopes],
    payload.orgId,
    payload.applicationId,
    payload.scopes,
    {
      token,
    }
  );

  yield put(loadedApplicationScopes(response));
}

export function* postAssignOrganisation({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);

  const response: Organisation = yield call(
    [orgsService, orgsService.postAssignOrganisation],
    payload.organisationId,
    payload.assignedToUpn,
    payload.assignedToDisplayName,
    {
      token,
    }
  );

  yield put(loadedOrganisation(response));
  yield put(refreshActivityFeed(payload.organisationId));
}

export function* postUnAssignOrganisation({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const response: Organisation = yield call(
    [orgsService, orgsService.unassign],
    payload.id,
    {
      token,
    }
  );

  yield put(loadedOrganisation(response));
  yield put(refreshActivityFeed(payload.id));
}

function* updateShareSaga({
  payload,
}: ActionPayload<{ id: string; share: ShareRecord }>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);
  const { id, share } = payload;

  const response: ShareRecord = yield call(
    [shareService, shareService.update],
    id,
    share,
    { token }
  );

  // const feedState: ReturnType<typeof feedSelector> = yield select(feedSelector);
  // const params = feedState?.[id]?.params;
  // yield put(refreshActivityFeed(id, params));

  yield put(refreshActivityFeed(id));
  yield put(loadedShare(response));
}

export function* rootSaga() {
  yield all([
    watchGetAllSaga(),
    watchGetOrganisationSaga(),
    watchPatchOrganisationSaga(),
    watchApproveChangeSaga(),
    watchPatchEngagementSaga(),
    watchPutEngagementEventSaga(),
    watchPatchEngagementEventSaga(),
    watchCloseApplicationSaga(),
    watchReopenApplicationSaga(),
    watchReleaseApplicationSaga(),
    watchSendMessageSaga(),
    watchAttachmentsSaga(),
    watchGetApplicationSaga(),
    watchPatchApplicationSaga(),
    watchApproveApplicationSaga(),
    watchRejectApplicationSaga(),
    watchWithdrawApplicationSaga(),
    watchExpansionApplicationListSaga(),
    watchGetOrganisationScopesSaga(),
    watchGetApplicationScopesSaga(),
    watchPostApplicationScopesSaga(),
    watchAssignOrganisationSaga(),
    watchUnAssignOrganisationSaga(),
    watchUpdateShareSaga(),
  ]);
}
